import { AsyncPipe, NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  input,
  output,
  signal,
  TemplateRef,
  untracked,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { RouterOutlet } from '@angular/router';
import { NgbOffcanvas, NgbTooltip } from '@ng-bootstrap/ng-bootstrap';
import { debounceTime, filter, fromEvent, map, switchMap } from 'rxjs';
import { delayFalsy, filterNil } from '../../../../utils/rxjs-operators';
import { capitalize } from '../../../../utils/string';
import { RoutingService } from '../../../_services/routing.service';
import { ScreenService } from '../../../_services/screen.service';
import { SwipeService } from '../../../_services/swipe.service';
import { SCROLL_UP_DISTANCE, SWIPE_X_THRESHOLD } from '../../../app.constants';
import { GlobalStore } from '../../../global.store';
import { NavigationStore } from '../../../navigation.store';
import { IconComponent } from '../../atoms/icon/icon.component';
import { SpinnerComponent } from '../../atoms/spinner/spinner.component';
import { PageBackgroundComponent } from '../../molecules';
import { MobileHeaderComponent } from '../mobile-header/mobile-header.component';
import { SidebarComponent } from '../sidebar/sidebar.component';

export const showSidebarSignal = signal(true);

@Component({
  selector: 'app-page',
  templateUrl: './page.component.html',
  styleUrls: ['./page.component.scss'],
  imports: [
    SidebarComponent,
    PageBackgroundComponent,
    RouterOutlet,
    AsyncPipe,
    NgTemplateOutlet,
    MobileHeaderComponent,
    IconComponent,
    SpinnerComponent,
    NgbTooltip,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [NgbOffcanvas],
})
export class PageComponent {
  readonly globalStore = inject(GlobalStore);
  readonly sidebarService = inject(NgbOffcanvas);
  readonly swipeService = inject(SwipeService);
  readonly screenService = inject(ScreenService);
  readonly routingService = inject(RoutingService);
  readonly host = inject(ElementRef);
  readonly navStore = inject(NavigationStore);

  readonly contentId = input.required<string>();

  readonly logout = output<void>();

  readonly contentElement =
    viewChild.required<ElementRef<HTMLDivElement>>('content');
  readonly innerContentElement =
    viewChild.required<ElementRef<HTMLDivElement>>('innerContent');
  readonly sidebarTemplate =
    viewChild.required<TemplateRef<SidebarComponent>>('sidebar');
  readonly sidebarElement =
    viewChild<ElementRef<HTMLElement>>('sidebarElement');
  readonly isMobile$ = this.screenService.isMobile$;
  readonly mobileHeaderTitle = computed(() => {
    const campaignName = this.globalStore.campaignName();
    return campaignName ? capitalize(campaignName) : '';
  });
  readonly isLoading = this.globalStore.isLoadingPage;
  readonly titleContainer =
    viewChild.required<ElementRef<HTMLDivElement>>('titleContainer');

  readonly contentScrollEvents$ = toObservable(this.innerContentElement).pipe(
    filterNil(),
    switchMap((innerContent) =>
      fromEvent<Event>(innerContent.nativeElement, 'scroll', {
        passive: true,
      }),
    ),
  );
  readonly showScrollupIndicator$ = this.contentScrollEvents$.pipe(
    debounceTime(100),
    map((event) => {
      const scrollTop = (event.target as HTMLDivElement | undefined)?.scrollTop;
      return (scrollTop ?? 0) > SCROLL_UP_DISTANCE;
    }),
  );

  readonly showScrollUpIndicator = signal(true);

  readonly pageSwipesRight$ = this.swipeService
    .getSwipeEvents(this.host)
    .pipe(filter((swipeDistance) => swipeDistance > SWIPE_X_THRESHOLD));

  readonly hasCampaignAdminPrivileges =
    this.globalStore.canPerformActionsOfRole('admin');
  readonly showSidebar = signal(this.screenService.isMobile() ? false : true);
  readonly canShowSidebar = computed(() => {
    const hasCampaign = !!this.globalStore.currentCampaign();
    const allowSidebarVisibility = showSidebarSignal();
    return allowSidebarVisibility && hasCampaign;
  });
  readonly homeUrl = computed(() =>
    this.routingService.getRoutePath('home', {
      campaign: this.globalStore.campaignName(),
    }),
  );
  readonly title = computed(() => this.navStore.currentRoute()?.title);

  readonly campaignBackgroundImage$ = toObservable(
    this.globalStore.currentCampaign,
  ).pipe(
    map((campaign) => campaign?.background_image),
    delayFalsy(1000),
  );

  constructor() {
    effect(() => {
      const loading = this.isLoading();
      const titleContainer = this.titleContainer();
      const title = untracked(() => this.title());
      if (!title || loading || !titleContainer) return;

      titleContainer.nativeElement.focus();
    });

    this.pageSwipesRight$
      .pipe(takeUntilDestroyed())
      .subscribe(() => this.openSidebar());

    /**
     * Due to the structure of the webpage (left sidebar, right a global content section)
     * where the scrollable element is not window or document or body, but instead the .page
     * element, none of the child-elements can know when a scroll event is triggered.
     * This propagates a scroll-event to the window, making it "global" in a sense.
     * That way, all child components (e.g. home.component) can listen to scroll events.
     */
    this.contentScrollEvents$
      .pipe(debounceTime(50), takeUntilDestroyed())
      .subscribe((event) => this.dispatchCustomPageScrollEvent(event));
  }

  closeSidebar() {
    if (!this.screenService.isMobile()) return;

    this.sidebarService.dismiss();
    this.showSidebar.set(false);
  }

  openSidebar() {
    if (!this.screenService.isMobile() || !this.canShowSidebar()) return;
    this.sidebarService.open(this.sidebarTemplate(), {
      ariaLabelledBy: 'offcanvas-basic-title',
    });
    this.showSidebar.set(true);
  }

  scrollToTop() {
    this.innerContentElement().nativeElement.scrollTo({
      top: 0,
      behavior: 'instant',
    });
  }

  private dispatchCustomPageScrollEvent(event: Event): void {
    const pageScrollEvent: CustomEvent = new CustomEvent('page.scroll', {
      ...event,
      detail: {
        pageElement: this.innerContentElement(),
      },
    });
    this.globalStore.fireScrollEvent(pageScrollEvent);
  }
}
